
import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Spinner,
  Input,
  Badge,
} from "reactstrap";

import {
  getComments,
  addComment,
  editComment,
  deleteComment,
  getTeammemberMention,
  CommentDTO,
  getUnreadCount,
} from "../../Api/ApiUrlConst";

import { useAuth } from "../../Context/AuthContext";

import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

interface CommentsProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: "project" | "task" | "subtask" | "childSubTask";
  entityId: number;
  title?: string;
  inlineMode?: boolean;
}

type CommentEntityType =
  | "project"
  | "task"
  | "subtask"
  | "childSubTask";

const Comments: React.FC<CommentsProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  title,
  inlineMode = false,
}) => {
  const { userID } = useAuth();

  const [comments, setComments] = useState<CommentDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const [newComment, setNewComment] = useState("");

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");

  const [teamMembers, setTeamMembers] = useState<
    { userID: string; name: string; department?: string }[]
  >([]);

  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [unreadCount, setUnreadCount] = useState(0);

  const commentInputRef = useRef<HTMLTextAreaElement | null>(null);

  // =========================================
  // LOAD
  // =========================================

  useEffect(() => {
    if (isOpen || inlineMode) {
      loadComments();
      loadTeamMembers();
      loadUnreadCount();
    }
  }, [isOpen, inlineMode, entityId, entityType]);

  if (!isOpen && !inlineMode) return null;

  const loadTeamMembers = async () => {
    try {
      const res = await getTeammemberMention();

      const members = res.data?.$values || res.data || [];

      setTeamMembers(
        members.map((m: any) => ({
          userID: m.userID || m.id,
          name: m.name,
          department: m.department || "",
        }))
      );
    } catch {
      setTeamMembers([]);
    }
  };

  const mapUnreadType = (
    type: CommentEntityType
  ): CommentEntityType => {
    switch (type) {
      case "task":
        return "task";

      case "subtask":
        return "subtask";

      case "childSubTask":
        return "childSubTask";

      case "project":
      default:
        return "project";
    }
  };

  const loadUnreadCount = async () => {
    try {
      const count = await getUnreadCount(
        mapUnreadType(entityType),
        entityId
      );

      setUnreadCount(count);
    } catch {
      setUnreadCount(0);
    }
  };

  const mapEntityType = (type: string) => {
    switch (type) {
      case "task":
        return "Task";

      case "subtask":
        return "SubTask";

      case "childSubTask":
        return "childSubTask";

      case "project":
      default:
        return "Project";
    }
  };

  const loadComments = async () => {
    setLoading(true);

    try {
      const res = await getComments(entityType, entityId);
      setComments(res || []);
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // ADD COMMENT
  // =========================================

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await addComment({
        entityType: mapEntityType(entityType),
        entityId,
        content: newComment,
      });

      setNewComment("");

      await loadComments();

      commentInputRef.current?.focus();
    } catch (e) {
      console.error(e);
    }
  };

  // =========================================
  // EDIT
  // =========================================

  const saveEdit = async () => {
    if (editingId === null) return;

    await editComment(editingId, editingText);

    setEditingId(null);
    setEditingText("");

    loadComments();
  };

  // =========================================
  // DELETE
  // =========================================

  const deleteCommentHandler = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    await deleteComment(deleteId);

    setShowDeleteModal(false);
    setDeleteId(null);

    loadComments();
  };

  // =========================================
  // AUTO RESIZE
  // =========================================

  const autoResize = () => {
    const ta = commentInputRef.current;

    if (!ta) return;

    ta.style.height = "auto";
    ta.style.height = ta.scrollHeight + "px";
  };

  // =========================================
  // MENTION
  // =========================================

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const val = e.target.value;

    setNewComment(val);

    const pos = commentInputRef.current?.selectionStart ?? 0;

    const start = val.lastIndexOf(" ", pos - 1) + 1;

    const token = val.slice(start, pos);

    if (token.startsWith("@")) {
      setMentionOpen(true);
      setMentionSearch(token.slice(1));
    } else {
      setMentionOpen(false);
      setMentionSearch("");
    }
  };

  const filteredMentions = teamMembers.filter((m) =>
    m.name.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  const insertMention = (name: string) => {
    const ta = commentInputRef.current;

    if (!ta) return;

    const cursor = ta.selectionStart ?? newComment.length;

    const before = newComment.slice(0, cursor);

    const after = newComment.slice(cursor);

    const mention = `${name.trim()} `;

    setNewComment(before + mention + after);

    setMentionOpen(false);

    setTimeout(() => {
      const pos = before.length + mention.length;

      ta.selectionStart = ta.selectionEnd = pos;

      ta.focus();
    }, 0);
  };

  // =========================================
  // RENDER MENTIONS
  // =========================================

  const renderCommentWithMentions = (text: string) => {
    if (!text) return null;

    const regex = /@([A-Za-z]+(?: [A-Za-z]+)*)/g;

    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (index % 2 === 0) {
        return <span key={index}>{part}</span>;
      }

      return (
        <span
          key={index}
          style={{
            background: "#e7f1ff",
            color: "#0d6efd",
            padding: "3px 6px",
            borderRadius: "6px",
            fontWeight: 600,
            marginRight: "4px",
          }}
        >
          @{part}
        </span>
      );
    });
  };

  // =========================================
  // 🔥 INLINE MODE
  // =========================================

  if (inlineMode) {
    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* COMMENTS */}
        <div
          style={{
            maxHeight: "300px",
            overflowY: "auto",
            paddingRight: "6px",
          }}
        >
          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : comments.length === 0 ? (
            <p className="text-muted text-center py-3">
              No comments yet.
            </p>
          ) : (
            comments.map((c) => (
              <div
                key={c.id}
                style={{
                  background: "#f7f8fa",
                  border: "1px solid #dfe1e6",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    color: "#172b4d",
                    marginBottom: "4px",
                  }}
                >
                  {c.createdByName}

                  <span
                    style={{
                      fontSize: "12px",
                      color: "#6b778c",
                      marginLeft: "8px",
                    }}
                  >
                    {new Date(c.createdAt).toLocaleString()}
                  </span>
                </div>

                <div style={{ whiteSpace: "pre-wrap" }}>
                  {renderCommentWithMentions(c.content)}
                </div>
              </div>
            ))
          )}
        </div>

        {/* INPUT */}
        <div
          style={{
            borderTop: "1px solid #ddd",
            marginTop: "10px",
            paddingTop: "10px",
          }}
        >
          <textarea
            ref={commentInputRef}
            value={newComment}
            onChange={(e) => {
              handleInputChange(e);
              autoResize();
            }}
            placeholder="Write a comment..."
            rows={2}
            className="form-control"
          />

          <div className="text-start mt-2">
            <Button
              color="primary"
              size="sm"
              onClick={handleAddComment}
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // =========================================
  // MODAL MODE
  // =========================================

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={onClose}
        size="xl"
        centered
      >
        <ModalHeader
          toggle={onClose}
          className="bg-primary text-white"
        >
          💬 Comments — {title}
        </ModalHeader>

        <ModalBody>
          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              <div
                style={{
                  maxHeight: 380,
                  overflowY: "auto",
                  paddingRight: "6px",
                }}
              >
                {comments.length === 0 ? (
                  <p className="text-muted text-center py-3">
                    No comments yet.
                  </p>
                ) : (
                  comments.map((c) => (
                    <div
                      key={c.id}
                      className="comment-bubble"
                    >
                      <div className="comment-header">
                        <div>
                          <div className="comment-name">
                            {c.createdByName}

                            <span className="comment-time">
                              {" "}
                              (
                              {new Date(
                                c.createdAt
                              ).toLocaleString()}
                              )
                            </span>
                          </div>
                        </div>

                        {!c.isDeleted &&
                          c.createdById === userID && (
                            <div className="comment-actions">
                              <Button
                                size="sm"
                                color="warning"
                                className="me-2"
                                onClick={() => {
                                  setEditingId(c.id);
                                  setEditingText(c.content);
                                }}
                              >
                                <EditNoteIcon />
                              </Button>

                              <Button
                                size="sm"
                                color="danger"
                                onClick={() =>
                                  deleteCommentHandler(c.id)
                                }
                              >
                                <DeleteOutlineIcon />
                              </Button>
                            </div>
                          )}
                      </div>

                      <div className="mt-2">
                        {c.isDeleted ? (
                          <div className="deleted-bubble">
                            🗑 This comment was deleted
                          </div>
                        ) : editingId === c.id ? (
                          <>
                            <Input
                              type="textarea"
                              value={editingText}
                              onChange={(e) =>
                                setEditingText(e.target.value)
                              }
                            />

                            <div className="text-end mt-2">
                              <Button
                                size="sm"
                                color="primary"
                                onClick={saveEdit}
                              >
                                Save Changes
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div
                              style={{
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {renderCommentWithMentions(
                                c.content
                              )}
                            </div>

                            {c.isEdited && (
                              <div className="edited-tag">
                                (edited)
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div
                className="comment-input-box"
                style={{
                  position: "relative",
                  marginTop: "16px",
                }}
              >
                <textarea
                  ref={commentInputRef}
                  value={newComment}
                  onChange={(e) => {
                    handleInputChange(e);
                    autoResize();
                  }}
                  placeholder="Write a comment..."
                  rows={1}
                  style={{
                    width: "100%",
                    overflow: "hidden",
                    resize: "none",
                  }}
                />

                {mentionOpen &&
                  filteredMentions.length > 0 && (
                    <div
                      className="mention-dropdown p-1 position-absolute w-100"
                      style={{
                        top: "100%",
                        zIndex: 10,
                      }}
                    >
                      {filteredMentions.map((m) => (
                        <div
                          key={m.userID}
                          className="p-2 mention-item"
                          onMouseDown={(e) =>
                            e.preventDefault()
                          }
                          onClick={() =>
                            insertMention(m.name)
                          }
                        >
                          <strong>{m.name}</strong>

                          <small className="text-muted">
                            ({m.department})
                          </small>
                        </div>
                      ))}
                    </div>
                  )}
              </div>

              <div className="text-end mt-3">
                <Button
                  outline
                  className="me-2"
                  onClick={onClose}
                >
                  Close
                </Button>

                <Button
                  color="primary"
                  onClick={handleAddComment}
                >
                  Add Comment
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      {/* DELETE MODAL */}

      <Modal
        isOpen={showDeleteModal}
        centered
        toggle={() => setShowDeleteModal(false)}
      >
        <ModalHeader
          toggle={() => setShowDeleteModal(false)}
        >
          Confirm Delete
        </ModalHeader>

        <ModalBody>
          <div className="text-center">
            <h5 className="text-danger mb-3">
              Are you sure?
            </h5>

            <p>
              This comment will be marked as deleted.
            </p>
          </div>
        </ModalBody>

        <div className="d-flex justify-content-end p-3">
          <Button
            className="me-2"
            outline
            onClick={() =>
              setShowDeleteModal(false)
            }
          >
            Cancel
          </Button>

          <Button
            color="danger"
            onClick={confirmDelete}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default Comments;

